import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { translate } from 'react-i18next';
import ReactHtmlParser from 'react-html-parser';
import Modal from 'component/Modal';
import TextField from 'component/TextField';
import ApiRequest from 'utils/ApiRequest';
import API from 'constants/api';
import 'container/priceBanner/styles.css';
import { isClubeLeroyThemeCode } from 'utils/priceBannerCsvWithStores';

/** Formata número para exibição decimal pt-BR (ex: 12.5 -> "12,50") */
const formatNumberToBr = (value) => {
  if (value === '' || value === null || value === undefined) return '';
  const num = typeof value === 'number' ? value : parseFloat(String(value).replace(',', '.'));
  if (Number.isNaN(num)) return '';
  return num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/** Converte string em formato BR para número (ex: "1.234,56" -> 1234.56) */
const parseBrToNumber = (str) => {
  if (!str || typeof str !== 'string') return NaN;
  const normalized = str
    .trim()
    .replace(/\s/g, '')
    .replace(/\./g, '')
    .replace(',', '.');
  return parseFloat(normalized);
};

/** Formata inteiros (apenas dígitos) com milhar pt-BR usando '.' */
const formatIntDigitsWithThousands = (digits) => {
  const d = String(digits || '').replace(/\D/g, '');
  if (!d) return '0';
  // Insere '.' a cada 3 dígitos a partir da direita
  return d.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

/** Formata entrada numérica do campo quantidade (milhar '.' e decimal ',') */
const formatQuantityInputToBr = (raw) => {
  if (raw === null || raw === undefined) return '';
  const input = String(raw);
  if (!input) return '';

  // Mantém apenas dígitos e separadores '.' e ','.
  const cleaned = input.replace(/[^0-9.,]/g, '');
  if (!cleaned) return '';

  const endsWithComma = cleaned.endsWith(',');
  const endsWithDot = cleaned.endsWith('.');

  let intDigits = '';
  let fracDigits = '';

  if (cleaned.includes(',')) {
    // Se houver vírgula, ela é o separador decimal; os '.' são de milhar.
    const lastCommaIdx = cleaned.lastIndexOf(',');
    intDigits = cleaned.slice(0, lastCommaIdx).replace(/\./g, '');
    fracDigits = cleaned.slice(lastCommaIdx + 1).replace(/[.,]/g, '');
  } else if (cleaned.includes('.')) {
    // Sem vírgula: heurística para diferenciar milhar '.' vs decimal '.'.
    const firstDotIdx = cleaned.indexOf('.');
    const lastDotIdx = cleaned.lastIndexOf('.');

    // Caso com múltiplos '.' (ex: 1.234.567) => milhar.
    if (firstDotIdx !== lastDotIdx) {
      intDigits = cleaned.replace(/\./g, '');
      fracDigits = '';
    } else {
      const digitsAfterDot = cleaned.slice(firstDotIdx + 1).replace(/[.,]/g, '');
      // Se tem 1-2 dígitos após o '.', trata como decimal (converte para ',').
      // Se tem 3+ dígitos, trata como milhar.
      if (digitsAfterDot.length > 0 && digitsAfterDot.length <= 2) {
        intDigits = cleaned.slice(0, firstDotIdx).replace(/\./g, '');
        fracDigits = digitsAfterDot;
      } else {
        intDigits = cleaned.replace(/\./g, '');
        fracDigits = '';
      }
    }
  } else {
    // Somente inteiro (ou usuário digitou algo que virou só dígitos).
    intDigits = cleaned.replace(/,/g, '').replace(/\./g, '');
    fracDigits = '';
  }

  // Sanitiza dígitos (não truncamos aqui; a validação bloqueia se exceder NUMERIC(10,3)).
  intDigits = intDigits.replace(/[^0-9]/g, '');
  if (!intDigits) intDigits = '0';

  fracDigits = fracDigits.replace(/[^0-9]/g, '');
  // Cap pequeno só para segurança de render; continua inválido se exceder 7/3.
  if (intDigits.length > 20) intDigits = intDigits.slice(0, 20);
  if (fracDigits.length > 10) fracDigits = fracDigits.slice(0, 10);

  const intFormatted = formatIntDigitsWithThousands(intDigits);

  if (endsWithComma) {
    return `${intFormatted},`;
  }

  // Se não há vírgula decimal e o usuário terminou com '.', mantemos o '.' para não "sumir".
  if (!cleaned.includes(',') && endsWithDot && fracDigits === '') {
    // Se o usuário deixou apenas o separador de milhar "pendurado" no final (ex: "10.000."),
    // não exibimos o '.' final para evitar "...." / ponto solto na UI.
    return intFormatted;
  }

  if (fracDigits !== '') {
    return `${intFormatted},${fracDigits}`;
  }

  // Sem parte decimal.
  return intFormatted;
};

const EditExtrasModal = ({
  open,
  onOpenChange,
  productData,
  user,
  translate,
  onSuccess
}) => {
  const [quantity, setQuantity] = useState('');
  const [discountPercent, setDiscountPercent] = useState('');
  const [initialQuantity, setInitialQuantity] = useState('');
  const [initialDiscountPercent, setInitialDiscountPercent] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({
    quantity: false,
    discountPercent: false
  });

  useEffect(() => {
    if (open && productData) {
      const lm = productData.lm || productData.productLm;
      const extras = productData.cmpmExtra || productData.productExtrasMap?.[lm];

      if (extras) {
        const q = extras.quantity?.toString() || '';
        const pv = formatNumberToBr(extras.discountPercent);
        setQuantity(formatQuantityInputToBr(q));
        setDiscountPercent(pv);
        setInitialQuantity(formatQuantityInputToBr(q));
        setInitialDiscountPercent(pv);
      } else {
        setQuantity('');
        setDiscountPercent('');
        setInitialQuantity('');
        setInitialDiscountPercent('');
      }
      setErrors({ quantity: false, discountPercent: false });
    }
  }, [open, productData]);

  const themeCode = productData?.themeCode
    || productData?.theme?.code
    || productData?.theme_code;
  const themeName = productData?.themeName
    || productData?.theme?.name;
  const isClubeLeroyTheme =
    isClubeLeroyThemeCode(themeCode) || themeName === 'Clube Leroy';

  const hasChanges =
    quantity !== initialQuantity ||
    discountPercent !== initialDiscountPercent;

  const isQuantityValid = (() => {
    if (isClubeLeroyTheme) return true;
    if (quantity === '' || quantity == null) return false;

    const parts = String(quantity).split(',');
    const intPart = (parts[0] || '').replace(/\./g, '');
    const decPart = parts.length > 1 ? (parts[1] || '') : '';

    if (!/^\d+$/.test(intPart)) return false;
    if (intPart.length < 1 || intPart.length > 7) return false;

    if (!/^\d*$/.test(decPart)) return false;
    if (decPart.length > 3) return false;

    const n = parseBrToNumber(String(quantity));
    if (!Number.isFinite(n) || n <= 0) return false;

    return true;
  })();

  const isDiscountPercentValid = (() => {
    if (!discountPercent || discountPercent.trim() === '') return false;
    const num = parseBrToNumber(discountPercent);
    if (Number.isNaN(num)) return false;
    return num >= 0.01 && num <= 99.99;
  })();

  const handleClose = () => {
    setQuantity('');
    setDiscountPercent('');
    setErrors({ quantity: false, discountPercent: false });
    onOpenChange(false);
  };

  const validateFields = () => {
    const numDiscount = parseBrToNumber(discountPercent);
    const newErrors = {
      quantity: isClubeLeroyTheme ? false : !isQuantityValid,
      discountPercent: !discountPercent || Number.isNaN(numDiscount) || numDiscount < 0.01 || numDiscount > 99.99
    };
    setErrors(newErrors);
    return !newErrors.quantity && !newErrors.discountPercent;
  };

  const handleSave = async () => {
    if (!validateFields()) {
      return;
    }

    setLoading(true);
    const token = user?.dsToken;
    if (!token) {
      alert('Erro: Token de autenticação não encontrado');
      setLoading(false);
      return;
    }

    try {
      const api = new ApiRequest(token);
      const lm = productData.lm || productData.productLm;

      const parsedQuantity = parseBrToNumber(String(quantity));
      const extraPayload = isClubeLeroyTheme
        ? { discountPercent: parseBrToNumber(discountPercent) }
        : {
          quantity: parsedQuantity,
          discountPercent: parseBrToNumber(discountPercent)
        };
      const payload = {
        idPriceBannerList: productData.idPriceBannerList,
        productLms: [lm],
        productExtrasByLm: {
          [lm]: extraPayload
        }
      };

      const response = await api.put(API.UPDATE_PRODUCT_EXTRAS, payload);

      if (response && response.cdStatus === 200) {
        if (onSuccess) onSuccess();
        handleClose();
      } else {
        alert(`Erro ao atualizar informações: ${  response?.message?.dsMessage || 'Erro desconhecido'}`);
      }
    } catch (error) {
      console.error('Erro ao atualizar extras:', error);
      alert('Erro ao atualizar informações complementares. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  const lm = productData?.lm || productData?.productLm || '';
  const description = productData?.description || productData?.productDescription || '';
  const listName = productData?.listName || productData?.nameList || '';
  const modalTitleKey = 'price.banner.products.edit.extras.title';
  const translatedModalTitle = translate(modalTitleKey);
  const modalTitle =
    translatedModalTitle && translatedModalTitle !== modalTitleKey
      ? translatedModalTitle
      : 'Editar Informações Complementares';

  return (
    <Modal
      title={modalTitle}
      size="md"
      isOpen={open}
      onCloseModal={handleClose}
      useControlledState
      className="pricebanner-modal edit-extras-modal"
      confirmButtonText={loading ? 'Salvando' : translate('save_button_label')}
      onConfirm={handleSave}
      isConfirmButtonDisabled={loading || !hasChanges || !isQuantityValid || !isDiscountPercentValid}
      noCloseOnConfirm
    >
      <div style={{ position: 'relative', width: '100%' }}>
        {loading && (
          <div className="edit-extras-loading-overlay" aria-hidden="false">
            <div className="edit-extras-loading-box">
              <div className="edit-extras-loading-icon" />
              <p className="edit-extras-loading-title">{translate('commonReport.loading.title') || 'Carregando as informações'}</p>
              <p className="edit-extras-loading-text">{ReactHtmlParser(translate('commonReport.loading.text') || 'O carregamento pode levar alguns minutos.<br />Por favor, aguarde.')}</p>
            </div>
          </div>
        )}
      <div className="edit-extras-modal-body">
        <div className="edit-extras-readonly">
          <div className="edit-extras-readonly-field">
            <span className="edit-extras-readonly-label">LM:</span>
            <div className="edit-extras-readonly-value">{lm}</div>
          </div>

          <div className="edit-extras-readonly-field">
            <span className="edit-extras-readonly-label">Descrição:</span>
            <div className="edit-extras-readonly-value">{description}</div>
          </div>

          <div className="edit-extras-readonly-field">
            <span className="edit-extras-readonly-label">Nome da Lista:</span>
            <div className="edit-extras-readonly-value">{listName}</div>
          </div>
        </div>

        {!isClubeLeroyTheme && (
          <div className="w-100 edit-extras-modal-fields" style={{ marginBottom: '15px', width: '100%' }}>
            <TextField
              required
              readOnly={false}
              disabled={loading}
              label="Quantidade de Unidades (A partir de)"
              placeholder="Digite a quantidade"
              name="quantity"
              fill
              type="text"
              inputMode="decimal"
              onKeyDown={(e) => {
                const allowedControlKeys = [
                  'Backspace',
                  'Delete',
                  'ArrowLeft',
                  'ArrowRight',
                  'ArrowUp',
                  'ArrowDown',
                  'Tab',
                  'Home',
                  'End',
                  'Enter'
                ];

                const isShortcut = e.ctrlKey || e.metaKey || e.altKey;
                const isNumberOrSeparator = /[0-9.,]/.test(e.key);
                const isAllowedControlKey = allowedControlKeys.includes(e.key);

                if (!isShortcut && !isNumberOrSeparator && !isAllowedControlKey) {
                  e.preventDefault();
                }
              }}
              onChange={(e) => {
                const rawValue = e.target.value || '';
                const formatted = formatQuantityInputToBr(rawValue);
                e.target.value = formatted;
                setQuantity(formatted);
                setErrors(prev => ({ ...prev, quantity: false }));
              }}
              translate={translate}
              value={quantity}
              validateValue={(v) => {
                const input = v === null || v === undefined ? '' : String(v);
                const parts = input.split(',');
                const intDigits = (parts[0] || '').replace(/\./g, '');
                const decPart = parts.length > 1 ? (parts[1] || '') : '';

                const shapeOk =
                  input !== '' &&
                  parts.length <= 2 &&
                  /^\d+$/.test(intDigits) &&
                  intDigits.length >= 1 &&
                  intDigits.length <= 7 &&
                  /^\d*$/.test(decPart) &&
                  decPart.length <= 3;

                const normalizedForParse = input
                  .trim()
                  .replace(/\s/g, '')
                  .replace(/\./g, '')
                  .replace(',', '.');
                const parsed = shapeOk ? parseFloat(normalizedForParse) : NaN;
                if (input === '' || v == null) return false;
                if (!shapeOk) return false;
                return Number.isFinite(parsed) && parsed > 0;
              }}
              errorText={(!isQuantityValid && quantity !== '') || errors.quantity ? 'Quantidade inválida' : undefined}
            />
          </div>
        )}

        <div className="w-100 edit-extras-modal-fields" style={{ marginBottom: '20px', width: '100%' }}>
          <TextField
            required
            readOnly={false}
            disabled={loading}
            label="Percentual de Desconto"
            placeholder="Digite o percentual de desconto"
            name="discountPercent"
            fill
            type="text"
            inputMode="decimal"
            controlled
            isControlledValue
            controlledValue={discountPercent ? `${discountPercent}%` : ''}
            onKeyDown={(e) => {
              const { key } = e;
              if (key !== 'Backspace' && key !== 'Delete') return;

              const current = discountPercent || '';
              if (!current) return;

              const displayed = `${current}%`;
              const { selectionStart, selectionEnd } = e.target;
              const start = typeof selectionStart === 'number' ? selectionStart : displayed.length;
              const end = typeof selectionEnd === 'number' ? selectionEnd : displayed.length;

              // Sem seleção: se o cursor estiver no final (após %) ou sobre o %,
              // remove diretamente o último dígito para não exigir mover o cursor.
              const noSelection = start === end;
              const cursorAfterPercent = start >= displayed.length;
              const cursorBeforePercent = start === displayed.length - 1;

              if (noSelection && (cursorAfterPercent || cursorBeforePercent)) {
                e.preventDefault();
                setDiscountPercent(prev => (prev ? prev.slice(0, -1) : ''));
                setErrors(prev => ({ ...prev, discountPercent: false }));
              }
            }}
            onChange={(e) => {
              let value = e.target.value || '';
              value = value.replace(/%/g, '');
              value = value.replace(/\./g, ',');
              value = value.replace(/[^0-9,]/g, '');
              const firstSepIndex = value.indexOf(',');
              if (firstSepIndex !== -1) {
                const before = value.slice(0, firstSepIndex + 1);
                const after = value.slice(firstSepIndex + 1).replace(/,/g, '');
                value = before + after;
              }
              const parts = value.split(',');
              const intPart = (parts[0] || '').slice(0, 3);
              const decPart = parts[1] ? parts[1].slice(0, 2) : '';
              value = parts.length > 1 ? `${intPart},${decPart}` : intPart;
              setDiscountPercent(value);
              e.target.value = value;
              setErrors(prev => ({ ...prev, discountPercent: false }));
            }}
            translate={translate}
            validateValue={(v) => {
              if (!v) return false;
              const regex = /^\d{1,3}([.,]\d{1,2})?$/;
              if (!regex.test(v)) return false;
              const num = parseBrToNumber(v);
              return !Number.isNaN(num) && num >= 0 && num <= 99.99;
            }}
            error={(!isDiscountPercentValid && discountPercent !== '') || errors.discountPercent}
            errorMsg='Percentual de desconto inválido'
            errorText={(!isDiscountPercentValid && discountPercent !== '') || errors.discountPercent ? 'Percentual de desconto inválido' : undefined}
          />
        </div>
      </div>
      </div>
    </Modal>
  );
};

EditExtrasModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  productData: PropTypes.object,
  user: PropTypes.object,
  translate: PropTypes.func.isRequired,
  onSuccess: PropTypes.func
};

export default translate('translations')(EditExtrasModal);
